import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  exportsApi,
  type ExportFormat,
  type ExportSourceType,
} from '@/lib/api/exports'

interface ExportModalProps {
  open: boolean
  onClose: () => void
  exportType: ExportSourceType
  sourceId?: string
  sourceIds?: string[]
  title?: string
}

const FORMATS: { id: ExportFormat; label: string }[] = [
  { id: 'markdown', label: 'Markdown' },
  { id: 'html', label: 'HTML' },
  { id: 'pdf', label: 'PDF' },
  { id: 'docx', label: 'DOCX' },
]

export function ExportModal({ open, onClose, exportType, sourceId, sourceIds, title }: ExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('markdown')
  const [status, setStatus] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!open) return null

  const handleExport = async () => {
    setBusy(true)
    setStatus('Generating export…')
    try {
      const batchIds = sourceIds?.filter(Boolean) ?? []
      const created =
        batchIds.length > 1
          ? await exportsApi.createBatchExport({ exportType, sourceIds: batchIds, format })
          : await exportsApi.createExport({
              exportType,
              sourceId: batchIds[0] ?? sourceId ?? '',
              format,
            })
      const ready =
        created.status === 'ready'
          ? created
          : await exportsApi.waitForReady(created.exportId)

      if (ready.status !== 'ready') {
        setStatus('Export failed')
        return
      }

      await exportsApi.download(ready.exportId)
      setStatus('Download started')
      onClose()
    } catch {
      setStatus('Export failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="export-modal"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg">
        <h2 className="text-lg font-semibold">Export</h2>
        {title ? <p className="mt-1 text-sm text-muted-foreground">{title}</p> : null}

        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium">Format</p>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((item) => (
              <Button
                key={item.id}
                type="button"
                size="sm"
                variant={format === item.id ? 'default' : 'outline'}
                onClick={() => setFormat(item.id)}
                data-testid={`export-format-${item.id}`}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>

        {status ? <p className="mt-3 text-sm text-muted-foreground">{status}</p> : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleExport()}
            disabled={busy}
            data-testid="export-submit"
          >
            {busy ? 'Exporting…' : 'Download'}
          </Button>
        </div>
      </div>
    </div>
  )
}
